import { useEffect, useState } from "react";
import CodeEditor from "../../components/BuilderComponents/CodeEditor/CodeEditor";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import Label from "../../components/FormComponents/Label";
import MultiSelectBox from "../../components/FormComponents/MultiSelectBox";
import { useAxiosContext } from "../../context/AxiosContext";

const ClusterManModal = ({ allApplication, editData, addDomain, editDomain }) => {
  const { axiosInstance, handleError } = useAxiosContext();

  const [selectedApps, setSelectedApps] = useState([]);
  const [externalApps, setExternalApps] = useState([]);
  const [fileInputs, setFileInputs] = useState({
    login_image: null,
    favicon: null,
    app_logo: null
  });
  const [samlXmlFile, setSamlXmlFile] = useState(null);
  const [domainData, setDomainData] = useState(editData);
  const [isSaving, setIsSaving] = useState(false);

  // handle domain data change
  const handledomainData = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setDomainData((prev) => ({
        ...prev,
        [name]: checked
      }));
      return;
    }

    setDomainData((prevDomainData) => {
      if (
        [
          "tenant_id",
          "client_id",
          "client_secret_id",
          "client_secret_value",
          "redirect_url_signin",
          "redirect_url_signup"
        ].includes(name)
      ) {
        return {
          ...prevDomainData,
          microsoft_auth: {
            ...prevDomainData.microsoft_auth,
            [name]: value
          }
        };
      } else {
        return {
          ...prevDomainData,
          [name]: value
        };
      }
    });
  };

  // save / update
  const handleSaveClusterDomain = async (type) => {
    try {
      setIsSaving(true);

      let uploadedImageData = {};

      for (const key of ["login_image", "favicon", "app_logo"]) {
        const file = fileInputs[key];
        if (file) {
          const formData = new FormData();
          formData.append("myFile", file);
          const response = await axiosInstance.post(`/global/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          uploadedImageData[key] = response.data.name;
        }
      }

      let updatedSamlConfig = { ...domainData.saml_config };
      if (samlXmlFile) {
        const formData = new FormData();
        formData.append("myFile", samlXmlFile);
        const response = await axiosInstance.post(`/global/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        updatedSamlConfig.saml_federation_xml = response.data.name;
      }

      const appsArray = selectedApps.map((data) => ({
        app_id: data.id,
        screen_name: []
      }));

      const newDomainData = {
        ...domainData,
        ...uploadedImageData,
        apps: appsArray,
        external_apps: externalApps,
        saml_config: updatedSamlConfig
      };

      if (type === "add") {
        addDomain(newDomainData);
      } else {
        editDomain({ ...editData, ...newDomainData }, editData.id);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  function multiSelectCallbackFunction(data) {
    setSelectedApps(data);
  }

  useEffect(() => {
    const selectedData = allApplication.filter((app) => {
      return editData.apps.some((editApp) => editApp.app_id === app.id);
    });
    setSelectedApps(selectedData);
    setExternalApps(editData.external_apps || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="builder_form space-y-6">
      {/* Section 1 - Basic Info */}
      <div className="d_flex flex_column gap_2">
        <h3>Basic Info</h3>
        <Input
          label="Domain"
          placeholder="Enter Domain"
          name="domain_name"
          value={domainData.domain_name || ""}
          onChange={handledomainData}
        />
        <Input
          label="Title"
          placeholder="Enter Title"
          name="title"
          value={domainData.title || ""}
          onChange={handledomainData}
        />
        <MultiSelectBox
          options={allApplication}
          onChange={multiSelectCallbackFunction}
          selectedData={selectedApps}
          label="Apps"
        />
        <ExternalAppsSection allApplication={allApplication} externalApps={externalApps} onChange={setExternalApps} />
        <Input
          type="checkbox"
          name="allow_notifications"
          checked={domainData.allow_notifications}
          onChange={handledomainData}
          label="Allow Notifications"
        />
        <Input
          type="checkbox"
          name="sandbox"
          checked={domainData.sandbox}
          onChange={handledomainData}
          label="Sandbox"
        />
      </div>

      {/* Section 2 - Branding */}
      <div className="d_flex flex_column gap_2">
        <h3>Branding</h3>
        <Input
          type="file"
          accept="image/*"
          name="login_image"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            setFileInputs((prev) => ({
              ...prev,
              login_image: e.target.files[0] || null
            }))
          }
          label={`Login Image : ${domainData?.login_image || ""}`}
        />

        <Input
          type="file"
          accept="image/*"
          name="favicon"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            setFileInputs((prev) => ({
              ...prev,
              favicon: e.target.files[0] || null
            }))
          }
          label={`Favicon : ${domainData?.favicon || ""}`}
        />

        <Input
          type="file"
          accept="image/*"
          name="app_logo"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            setFileInputs((prev) => ({
              ...prev,
              app_logo: e.target.files[0] || null
            }))
          }
          label={`App Logo : ${domainData?.app_logo || ""}`}
        />
        <Input
          type="checkbox"
          name="enable_custom_login_html"
          checked={domainData?.enable_custom_login_html}
          onChange={handledomainData}
          label="Enable Custom Login HTML"
        />
        {domainData?.enable_custom_login_html && (
          <div className="form_element_wrapper_text">
            <Label>Custom Login HTML</Label>
            <CodeEditor
              value={domainData?.custom_login_html}
              height="30vh"
              handleChange={(code) =>
                setDomainData({
                  ...domainData,
                  custom_login_html: code
                })
              }
            />
          </div>
        )}
      </div>

      {/* Section 3 - Authentication */}
      <div className="d_flex flex_column gap_2">
        <h3>Authentication</h3>

        <Input
          type="checkbox"
          name="enable_microsoft_auth"
          checked={domainData?.enable_microsoft_auth}
          onChange={handledomainData}
          label="Enable Microsoft Auth"
        />
        {domainData?.enable_microsoft_auth && (
          <>
            <p className="helper-text">Enter Microsoft OAuth details to enable login.</p>
            <MicrosoftAuthSection
              authData={domainData?.microsoft_auth}
              onChange={(updatedAuthData) =>
                setDomainData((prev) => ({
                  ...prev,
                  microsoft_auth: updatedAuthData
                }))
              }
            />
          </>
        )}

        <Input
          type="checkbox"
          name="enable_saml"
          checked={domainData?.enable_saml}
          onChange={handledomainData}
          label="Enable SAML Auth"
        />
        {domainData?.enable_saml && (
          <>
            <p className="helper-text">Upload your SAML XML file and configure SAML login.</p>
            <SamlAuthSection
              samlConfig={domainData?.saml_config}
              onChange={(updatedSamlConfig) =>
                setDomainData((prev) => ({
                  ...prev,
                  saml_config: updatedSamlConfig
                }))
              }
              onXmlFileSelect={(file) => setSamlXmlFile(file || null)}
            />
            {samlXmlFile && <p>Selected File: {samlXmlFile.name}</p>}
          </>
        )}
      </div>

      {/* Action button */}
      <div className="flex gap-4">
        <Button
          disabled={!domainData.domain_name || !domainData.title || isSaving}
          onClick={() => handleSaveClusterDomain(editData.id.length === 0 ? "add" : "save")}
        >
          {isSaving ? "Saving..." : editData.id.length === 0 ? "+ Add Domain" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

const ExternalAppsSection = ({ allApplication, externalApps, onChange }) => {
  const handleAppChange = (index, appId) => {
    const updated = externalApps.map((item, i) => (i === index ? { ...item, app_id: appId } : item));
    onChange(updated);
  };

  const handleRedirectUrlChange = (index, value) => {
    const updated = externalApps.map((item, i) => (i === index ? { ...item, redirect_url: value } : item));
    onChange(updated);
  };

  const handleAdd = () => {
    onChange([...externalApps, { app_id: "", redirect_url: "" }]);
  };

  const handleRemove = (index) => {
    onChange(externalApps.filter((_, i) => i !== index));
  };

  return (
    <div className="d_flex flex_column gap_2">
      <Label>External Apps</Label>
      {externalApps.map((item, index) => (
        <div key={index} className="d_flex gap_2" style={{ alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <Label>App</Label>
            <select value={item.app_id} onChange={(e) => handleAppChange(index, e.target.value)} className="form_input">
              <option value="">Select App</option>
              {allApplication.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 2 }}>
            <Input
              label="Redirect URL"
              placeholder="Enter Redirect URL"
              value={item.redirect_url}
              onChange={(e) => handleRedirectUrlChange(index, e.target.value)}
            />
          </div>
          <Button onClick={() => handleRemove(index)} style={{ flexShrink: 0 }}>
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button onClick={handleAdd}>+ Add External App</Button>
      </div>
    </div>
  );
};

const MicrosoftAuthSection = ({ authData = {}, onChange }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedAuthData = {
      ...authData,
      [name]: value
    };

    onChange(updatedAuthData); // Pass the full updated object
  };

  return (
    <>
      <Input
        label="Tenant Id"
        placeholder="Enter Tenant Id"
        name="tenant_id"
        value={authData.tenant_id || ""}
        onChange={handleChange}
      />
      <Input
        label="Client Id"
        placeholder="Enter Client Id"
        name="client_id"
        value={authData.client_id || ""}
        onChange={handleChange}
      />
      <Input
        label="Client Secret Id"
        placeholder="Enter Client Secret Id"
        name="client_secret_id"
        value={authData.client_secret_id || ""}
        onChange={handleChange}
      />
      <Input
        label="Client Secret Value"
        placeholder="xxxxxxxxxxxxxxxxxxx"
        name="client_secret_value"
        value={authData.client_secret_value || ""}
        onChange={handleChange}
      />
      <Input
        label="Redirect URL (Sign In)"
        placeholder="Enter Redirect URL for Sign In"
        name="redirect_url_signin"
        value={authData.redirect_url_signin || ""}
        onChange={handleChange}
      />
      <Input
        label="Redirect URL (Sign Up)"
        placeholder="Enter Redirect URL for Sign Up"
        name="redirect_url_signup"
        value={authData.redirect_url_signup || ""}
        onChange={handleChange}
      />
    </>
  );
};

const SamlAuthSection = ({ samlConfig = {}, onChange, onXmlFileSelect }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedSaml = {
      ...samlConfig,
      [name]: value
    };

    onChange(updatedSaml);
  };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    onXmlFileSelect(file);
  };

  return (
    <>
      <Input
        label="SAML Root URL"
        placeholder="Enter SAML Root URL"
        name="saml_root_url"
        value={samlConfig.saml_root_url || ""}
        onChange={handleChange}
      />
      <Input
        label="SAML Callback URL"
        placeholder="Enter Callback URL"
        name="saml_callback_url"
        value={samlConfig.saml_callback_url || ""}
        onChange={handleChange}
      />
      <Input
        label="SAML Federation URL"
        placeholder="Enter Federation URL"
        name="saml_federation_url"
        value={samlConfig.saml_federation_url || ""}
        onChange={handleChange}
      />
      <Input
        type="file"
        accept=".xml"
        name="saml_federation_xml"
        label={`Federation XML : ${samlConfig.saml_federation_xml || "Upload .xml file"}`}
        onClick={(e) => e.stopPropagation()}
        onChange={handleFileChange}
      />
    </>
  );
};

export default ClusterManModal;
